package com.eladfinish.bible.contest.screens.lexicographicalSort.ui

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.SnackbarDuration
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.lexicographical_sort_email_pdf
import com.eladfinish.bible.contest.lexicographical_sort_save_pdf
import com.eladfinish.bible.contest.platform.isDesktop
import com.eladfinish.bible.contest.platform.isWeb
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.OutputChapters
import com.eladfinish.bible.contest.screens.lexicographicalSort.LexicographicalSortPdfViewerAction
import com.eladfinish.bible.contest.screens.lexicographicalSort.LexicographicalSortPdfViewerEffect
import com.eladfinish.bible.contest.screens.lexicographicalSort.LexicographicalSortPdfViewerState
import com.eladfinish.bible.contest.screens.lexicographicalSort.LexicographicalSortPdfViewerStore
import com.eladfinish.bible.contest.ui.common.PreviewTheme
import com.eladfinish.bible.contest.ui.common.SetScreenIsLoading
import com.eladfinish.bible.contest.ui.components.WebView
import com.eladfinish.bible.contest.ui.components.buttons.PrimaryButtonText
import com.eladfinish.bible.contest.ui.components.dialogs.PdfEmailInputDialog
import com.eladfinish.bible.contest.ui.extensions.navigationBarInsetPadding
import dev.icerock.moko.resources.compose.stringResource
import dev.icerock.moko.resources.desc.StringDesc
import dev.icerock.moko.resources.desc.desc
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun LexicographicalSortPdfViewerScreen(
    store: LexicographicalSortPdfViewerStore = koinViewModel(),
    showToast: (message: String) -> Unit,
    showLoadingSnackbar: (message: String) -> Unit,
    dismissSnackbar: () -> Unit,
    showSnackbar: (
        message: StringDesc,
        actionLabel: StringDesc?,
        duration: SnackbarDuration,
        onActionPerformed: () -> Unit,
    ) -> Unit,
) {
    val state by store.observeState().collectAsState()

    LaunchedEffect(Unit) {
        store.observeSideEffect().collect { effect ->
            when (effect) {
                is LexicographicalSortPdfViewerEffect.ShowToast -> showToast(effect.text)
                is LexicographicalSortPdfViewerEffect.ShowLoadingSnackbar -> showLoadingSnackbar(effect.text)
                is LexicographicalSortPdfViewerEffect.DismissSnackbar -> dismissSnackbar()
                is LexicographicalSortPdfViewerEffect.ShowPdfSavedSnackbar -> {
                    showSnackbar(
                        "הקובץ נשמר: ${effect.pdfName}".desc(),
                        if (effect.canOpen) "פתיחה".desc() else null,
                        SnackbarDuration.Long,
                    ) {
                        store.dispatch(LexicographicalSortPdfViewerAction.OpenPdf(effect.pdfUri))
                    }
                }
            }
        }
    }

    LexicographicalSortPdfViewerContent(
        state = state,
        onAction = store::dispatch,
    )

    SetScreenIsLoading(state.isLoading)

    val dialogState = state.pdfEmailDialogState
    if (dialogState is LexicographicalSortPdfViewerState.PdfEmailDialogState.Visible) {
        PdfEmailInputDialog(
            initialFirstName = dialogState.firstName,
            initialLastName = dialogState.lastName,
            initialEmail = dialogState.email,
            initialSaveDetails = dialogState.saveDetails,
            onDismiss = { store.dispatch(LexicographicalSortPdfViewerAction.PdfEmailDialogDismissed) },
            onConfirm = { firstName, lastName, email, saveDetails ->
                store.dispatch(
                    LexicographicalSortPdfViewerAction.PdfEmailConfirmed(
                        firstName = firstName,
                        lastName = lastName,
                        email = email,
                        saveDetails = saveDetails,
                    )
                )
            },
        )
    }
}

@Composable
private fun LexicographicalSortPdfViewerContent(
    state: LexicographicalSortPdfViewerState,
    onAction: (LexicographicalSortPdfViewerAction) -> Unit,
) {
    Column(modifier = Modifier.fillMaxSize()) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f),
            contentAlignment = Alignment.Center,
        ) {
            when (val htmlState = state.htmlState) {
                is LexicographicalSortPdfViewerState.HtmlState.Loading -> {
                    CircularProgressIndicator()
                }
                is LexicographicalSortPdfViewerState.HtmlState.Ready -> {
                    if (isDesktop) {
                        // Desktop has no WebView to show the HTML in.
                        LexicographicalTableView(
                            table = htmlState.previewTable,
                            modifier = Modifier.fillMaxSize(),
                        )
                    } else {
                        WebView(
                            baseUrl = "file:///android_asset/",
                            htmlContent = htmlState.previewHtml,
                            modifier = Modifier.fillMaxSize(),
                        )
                    }
                }
                is LexicographicalSortPdfViewerState.HtmlState.Error -> {
                    Text(text = htmlState.message)
                }
            }
        }

        HorizontalDivider()

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .navigationBarInsetPadding()
                .padding(horizontal = 16.dp, vertical = 8.dp),
            contentAlignment = Alignment.Center,
        ) {
            when (state.outputChapters) {
                // A single chapter is saved to the device as a full PDF.
                OutputChapters.SINGLE -> if (state.isLoading) {
                    CircularProgressIndicator()
                } else {
                    PrimaryButtonText(
                        // Web has no "device" to save to — the file is downloaded via the
                        // browser, so it gets its own wording instead of the shared resource
                        // string used by Android/iOS.
                        text = if (isWeb) "הורדה כקובץ PDF" else stringResource(SharedRes.strings.lexicographical_sort_save_pdf),
                        onClick = { onAction(LexicographicalSortPdfViewerAction.SavePdfClicked) },
                    )
                }
                // Larger outputs (by mikud / whole book) are sent to the user's email.
                OutputChapters.BY_MIKUD,
                OutputChapters.ALL -> PrimaryButtonText(
                    text = stringResource(SharedRes.strings.lexicographical_sort_email_pdf),
                    onClick = { onAction(LexicographicalSortPdfViewerAction.EmailPdfClicked) },
                )
            }
        }
    }
}

@Preview
@Composable
private fun LexicographicalSortPdfViewerLoadingPreview() {
    PreviewTheme {
        LexicographicalSortPdfViewerContent(
            state = LexicographicalSortPdfViewerState(
                htmlState = LexicographicalSortPdfViewerState.HtmlState.Loading,
                savedPdfName = null,
                savedPdfUri = null,
                canOpenPdf = false,
                isLoading = false,
                pdfEmailDialogState = LexicographicalSortPdfViewerState.PdfEmailDialogState.Hidden,
                outputChapters = OutputChapters.SINGLE,
            ),
            onAction = {},
        )
    }
}
